import { Button, Stack } from "@mui/material";
import type { Cell } from "@silevis/reactgrid";
import { ReactGrid } from "@silevis/reactgrid";
import type { FC, Ref } from "react";
import { memo, useMemo } from "react";
import CombinationResources from "./CombinationResources";
import type { ParticipantsForCombination, Person } from "./getRoomsCells";
import { getRoomsCells } from "./getRoomsCells";
import { getHeaderCells } from "./headerCells";
import type {
  ParticipantListCombinationResponse,
  ParticipantListRequiredFields,
  ParticipantListTemplateCombination,
} from "./participantListApi";
import { useColumns } from "./useColumns";

interface Props {
  readonly combination: ParticipantListTemplateCombination;
  readonly requiredFields: ParticipantListRequiredFields;
  readonly participants: ParticipantsForCombination;
  readonly previousParticipants: ParticipantListCombinationResponse | undefined;
  readonly onParticipantsChange: (
    roomType: string,
    roomIdx: number,
    personIdx: number,
    update: Partial<Person> | null,
  ) => void;
  readonly onCopyFromFirstCombination?: () => void;
}

const ParticipantListCombination: FC<Props> = ({
  combination,
  requiredFields,
  participants,
  previousParticipants,
  onParticipantsChange,
  onCopyFromFirstCombination,
}) => {
  const { ref, columns, handleResizeColumn } = useColumns(requiredFields);

  const roomsCells: Cell[] = useMemo(
    () => getRoomsCells(combination, requiredFields, participants, previousParticipants, onParticipantsChange),
    [combination, requiredFields, participants, previousParticipants, onParticipantsChange],
  );
  const cells: Cell[] = useMemo(() => [...getHeaderCells(requiredFields), ...roomsCells], [requiredFields, roomsCells]);

  return (
    <Stack direction="column" spacing={2} ref={ref as Ref<HTMLDivElement>}>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
        }}
      >
        <CombinationResources rooms={combination.rooms} />
        {onCopyFromFirstCombination && <Button onClick={onCopyFromFirstCombination}>Kopiuj z pierwszego układu</Button>}
      </Stack>
      <ReactGrid
        columns={columns}
        cells={cells}
        stickyTopRows={1}
        stickyLeftColumns={2}
        initialFocusLocation={{ rowIndex: 1, colIndex: 2 }}
        onResizeColumn={handleResizeColumn}
      />
    </Stack>
  );
};

export default memo(ParticipantListCombination);
